'use client';

import { VStack, HStack, Box, Divider } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Clipboard } from '../../components/clipboard';
import { Button } from '../../components/button';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Copy01Icon,
  Refresh04Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../components/icon-button';
import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import { useState } from 'react';

interface SmsDisplayProps {
  phoneNumber: string;
  disabled?: boolean;
  messages?: MfaMessageDto[];
  isLoadingMessages?: boolean;
  onFetchMessages?: () => void;
}

export function SmsDisplay({
  phoneNumber,
  disabled = false,
  messages = [],
  isLoadingMessages = false,
  onFetchMessages,
}: SmsDisplayProps) {
  const t = useTranslations('designSystem.mfa.smsDisplay');
  const tMfa = useTranslations('designSystem.mfa');
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(false);

  return (
    <VStack alignItems="stretch" gap={4}>
      {/* Phone Number Section */}
      <VStack alignItems="stretch" gap={3}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {t('generatedPhoneNumber')}
          </Text>
          {disabled && (
            <Text fontSize="sm" color="fg.muted">
              {tMfa('disabled')}
            </Text>
          )}
        </HStack>

        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
        >
          <HStack justifyContent="space-between" alignItems="center">
            <Text
              fontSize="lg"
              fontWeight="medium"
              fontFamily="mono"
              color={disabled ? 'fg.disabled' : 'fg.default'}
            >
              {disabled ? '••••••••••••••' : phoneNumber}
            </Text>

            <Clipboard.Root value={phoneNumber}>
              <Clipboard.Trigger asChild>
                <IconButton
                  variant="outline"
                  size="sm"
                  disabled={!phoneNumber || disabled}
                >
                  <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                    <Copy01Icon size={16} />
                  </Clipboard.Indicator>
                </IconButton>
              </Clipboard.Trigger>
            </Clipboard.Root>
          </HStack>
        </Box>

        <Text fontSize="xs" color="fg.muted" textAlign="center">
          {t('usePhoneForMfa')}
        </Text>
      </VStack>

      {/* Latest Messages Section */}
      {onFetchMessages && (
        <>
          <Divider />

          <VStack alignItems="stretch" gap={3}>
            <HStack justifyContent="space-between" alignItems="center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMessagesExpanded(!isMessagesExpanded)}
                aria-label={
                  isMessagesExpanded ? t('hideMessages') : t('showMessages')
                }
              >
                <HStack gap={2} alignItems="center">
                  <Text fontSize="sm" fontWeight="medium" color="fg.default">
                    {t('latestSmsMessages')}
                  </Text>
                  {isMessagesExpanded ? (
                    <ArrowUp01Icon size={16} />
                  ) : (
                    <ArrowDown01Icon size={16} />
                  )}
                </HStack>
              </Button>
              {isMessagesExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onFetchMessages}
                  disabled={isLoadingMessages || disabled}
                  aria-label={t('refreshMessages')}
                >
                  <Refresh04Icon
                    size={14}
                    style={{
                      animation: isLoadingMessages
                        ? 'spin 1s linear infinite'
                        : 'none',
                    }}
                  />
                  {t('refresh')}
                </Button>
              )}
            </HStack>

            {/* Messages Display */}
            {isMessagesExpanded && (
              <>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="bg.muted"
                  borderWidth="1px"
                  borderColor="border.muted"
                  minHeight="120px"
                >
                  {isLoadingMessages ? (
                    <VStack
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Text fontSize="sm" color="fg.muted">
                        {t('loadingMessages')}
                      </Text>
                    </VStack>
                  ) : messages.length === 0 ? (
                    <VStack
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Text fontSize="sm" color="fg.muted" textAlign="center">
                        {t('noSmsMessages')}
                      </Text>
                    </VStack>
                  ) : (
                    <VStack alignItems="stretch" gap={3}>
                      {messages.slice(0, 3).map((message) => (
                        <Box
                          key={message.id}
                          p={3}
                          bg="bg.default"
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor="border.muted"
                        >
                          <VStack alignItems="stretch" gap={2}>
                            <HStack
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <VStack alignItems="flex-start" gap={0.5}>
                                <Text fontSize="xs" color="fg.muted">
                                  {new Date(
                                    message.receivedAt,
                                  ).toLocaleString()}
                                </Text>
                                {message.from && (
                                  <Text
                                    fontSize="xs"
                                    color="fg.muted"
                                    fontFamily="mono"
                                  >
                                    {t('from', { sender: message.from })}
                                  </Text>
                                )}
                              </VStack>
                              <Clipboard.Root value={message.content}>
                                <Clipboard.Trigger asChild>
                                  <IconButton variant="ghost" size="xs">
                                    <Clipboard.Indicator
                                      copied={<Tick01Icon size={12} />}
                                    >
                                      <Copy01Icon size={12} />
                                    </Clipboard.Indicator>
                                  </IconButton>
                                </Clipboard.Trigger>
                              </Clipboard.Root>
                            </HStack>
                            <Text
                              fontSize="sm"
                              color="fg.default"
                              fontFamily="mono"
                              lineHeight="1.4"
                              maxHeight="120px"
                              overflowY="auto"
                              whiteSpace="pre-wrap"
                              wordBreak="break-word"
                            >
                              {message.content}
                            </Text>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Text fontSize="xs" color="fg.muted" textAlign="center">
                  {t('showingLatest')}
                </Text>
              </>
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
}

export type { SmsDisplayProps };
